import { useCallback, useRef } from 'react';

export function useScrollToTranscriptItem(itemIdPrefix: string) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToTranscriptItem = useCallback(
    (index: number) => {
      const child = scrollRef.current?.querySelector(`#${itemIdPrefix}${index}`);
      if (scrollRef.current && child) {
        scrollIntoViewCentered(scrollRef.current, child as HTMLElement);
      }
    },
    [itemIdPrefix]
  );

  return { scrollRef, scrollToTranscriptItem };
}

/**
 * Scrolls the container to center the element within the container
 *
 * @param container The container to scroll
 * @param element The element to center within the container
 */
export const scrollIntoViewCentered = (container: HTMLElement, element: HTMLElement) => {
  /* DEVNOTE:
   *   Although a `scrollIntoView` native method exists (with the ability to scroll the item to 'center'),
   *   it tries to scroll **all ascendent containers** to make the child element visible in the middle of the screen,
   *   which means that if the window is also scrollable, it tries to scroll that one too (and any other containers in between).
   *   This is not the desired behavior, so we have to implement our own version of the scrollIntoView method.
   */

  // Calculate the position of the element relative to the container
  const elementTop =
    element.getBoundingClientRect().top - // Top relative to the viewport, so it will change as container is scrolled
    container.getBoundingClientRect().top +
    container.scrollTop; // Tells us how much the container has been scrolled so far, so we offset the element's top

  // Calculate the scroll position needed to center the element within the container
  const scrollToPosition = elementTop - container.clientHeight / 2 + element.offsetHeight / 2;

  // Scroll the container to the calculated position
  container.scrollTo({
    top: scrollToPosition,
    behavior: 'smooth',
  });
};
